import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { changeVehicleCreators, changeVehicleSelectors, middlewareMgr } from '../../store';
import { DEFAULT_CARD_INDEX, RANK_INDEX_OFFSET } from '../../constants';
import { useValidateVehicle } from '../../utils';
import {
    createCancelVehicleUpdateEvent,
    createBuildVehicleUpdateEvent,
    createNewScenarioEvent,
    createNewScenarioFromResultListEvent,
    createSelectVehicleEvent,
    createSelectVehicleFromResultListEvent
} from '../../analytics';
import { useVehicleSearchFiltersContext } from '../changeVehicle/VehicleSearchFiltersContext';
import { useAddNewScenarioAvailability } from '../changeVehicle/useAddNewScenarioAvailability';

import type { RetentionDecisions } from '../../types';
import type { ChangeVehicleFooterInterstateProps } from './ChangeVehicleFooter.interstate.types';

export const useChangeVehicleFooter = ({
    isInsideSlideOut,
    onCancel,
    onVehicleSelected
}: Pick<ChangeVehicleFooterInterstateProps, 'isInsideSlideOut' | 'onCancel' | 'onVehicleSelected'>) => {
    const dispatch = useDispatch();
    const { appliedFilters } = useVehicleSearchFiltersContext();

    const selectedVehicle = useSelector(changeVehicleSelectors.getSelectedVehicleWithCustomData);
    const selectedVehicleWithIndex = useSelector(changeVehicleSelectors.getSelectedVehicle);
    const isChangeProcessing = useSelector(changeVehicleSelectors.isChangeProcessing);
    const agentPromptString = useSelector(changeVehicleSelectors.getAgentPromptString);
    const isVehicleValid = useValidateVehicle(selectedVehicle);

    // Drives which of the vs-023/vs-024 (AVO) vs vs-028/vs-027 (everyone else) tag pair fires below. An AVO
    // recommendation carries recommendedPayment (set by mapAiAltVehicleOptionToVehicle), nothing else does.
    const isAvoSelection = Boolean((selectedVehicleWithIndex as { recommendedPayment?: unknown }).recommendedPayment);

    // The limit is shared with the result rows so the two cannot disagree about it; the validity half is this footer's
    // own, and is the same check that gates Change — a vehicle the dealer cannot apply is not one to seed a scenario
    // from either.
    const { isScenarioLimitReached } = useAddNewScenarioAvailability();
    const isAddNewScenarioDisabled = !isVehicleValid || isScenarioLimitReached;

    const handleAddNewScenarioClick = useCallback(() => {
        // Every New Scenario button ends up here — the result rows, the alternative vehicle options card and the
        // build-vehicle card all reach it through useRowActionRequests. So
        // this is the one place that picks between the two tags. Each caller has already decided the action is
        // allowed: useRowActionRequests checks before calling here.
        const newScenarioEvent = isAvoSelection
            ? createNewScenarioEvent(selectedVehicle)
            : createNewScenarioFromResultListEvent(selectedVehicle);
        dispatch(middlewareMgr.hostActions.forwardCDLEvent(newScenarioEvent.payload));

        dispatch(changeVehicleCreators.addNewScenarioClicked(selectedVehicle));

        dispatch(changeVehicleCreators.resetOnAddNewScenario());

        // Change Vehicle is a slideout over the dashboard in MV, so navigating the route to /dashboard
        // doesn't dismiss it — close the slideout via onCancel (mirroring handleCancelClick). Fall back to
        // route navigation for non-slideout hosts.
        if (isInsideSlideOut && onCancel) {
            onCancel();
        } else {
            dispatch(middlewareMgr.hostActions.navigateToDashboard());
        }
    }, [dispatch, selectedVehicle, isAvoSelection, isInsideSlideOut, onCancel]);

    const handleCancelClick = () => {
        const resultsRank = (selectedVehicleWithIndex.cardIndex ?? DEFAULT_CARD_INDEX) + RANK_INDEX_OFFSET;
        const analyticsEvent = createCancelVehicleUpdateEvent(selectedVehicleWithIndex, appliedFilters, resultsRank);
        dispatch(analyticsEvent);

        dispatch(changeVehicleCreators.vehicleChangeCancel());

        if (isInsideSlideOut && onCancel) {
            onCancel();
        } else {
            dispatch(middlewareMgr.hostActions.navigateToDashboard());
        }
    };

    const changeVehicle = useCallback(
        async (carryOverDiscounts: boolean, retainedItems: RetentionDecisions | undefined) => {
            // Opt-out: hand the host the pick instead of committing it.
            if (onVehicleSelected) {
                onVehicleSelected(selectedVehicle);
                return;
            }

            try {
                const resultsRank = (selectedVehicleWithIndex.cardIndex ?? DEFAULT_CARD_INDEX) + RANK_INDEX_OFFSET;

                const isBuildVehicle =
                    !!selectedVehicleWithIndex.chromeStyleId &&
                    !(selectedVehicleWithIndex.vin && selectedVehicleWithIndex.stockNumber);

                if (isBuildVehicle) {
                    const buildUpdateEvent = createBuildVehicleUpdateEvent(
                        selectedVehicleWithIndex,
                        resultsRank,
                        agentPromptString || ''
                    );
                    const hostAction = middlewareMgr.hostActions.forwardCDLEvent(buildUpdateEvent.payload);
                    dispatch(hostAction);
                } else if (isAvoSelection) {
                    // An AVO card apply emits vs-024 (Select Vehicle), not vs-027 (the inline, non-AVO equivalent).
                    dispatch(middlewareMgr.hostActions.forwardCDLEvent(createSelectVehicleEvent(selectedVehicleWithIndex).payload));
                } else {
                    // The inline per-row/card "Select Vehicle" button raised this via useRowActionRequests — vs-027
                    const analyticsEvent = createSelectVehicleFromResultListEvent(selectedVehicleWithIndex);
                    dispatch(middlewareMgr.hostActions.forwardCDLEvent(analyticsEvent.payload));
                }

                dispatch(changeVehicleCreators.changeVehicleClicked(selectedVehicle, carryOverDiscounts, retainedItems));
                dispatch(middlewareMgr.hostActions.switchVehicle(selectedVehicle));
            } catch (error) {
                // Error handling - no analytics tracking
            }
        },
        [dispatch, selectedVehicle, selectedVehicleWithIndex, isAvoSelection, agentPromptString, onVehicleSelected]
    );

    return {
        selectedVehicle,
        isChangeProcessing,
        isVehicleValid,
        handleCancelClick,
        changeVehicle,
        isAddNewScenarioDisabled,
        handleAddNewScenarioClick
    };
};
